import React, { useState } from 'react';
import styles from './UserCard.module.css';
import Button from '../../common/Button/Button';

const UserCard = ({ user, onEdit, onDelete, onView }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Add null/undefined check to prevent destructuring error
  if (!user) {
    console.error('UserCard: user prop is undefined or null');
    return (
      <div className={styles.userCard}>
        <div className={styles.errorState}>
          <div className={styles.errorIcon}>⚠️</div>
          <p>Error: User data not available</p>
          <p className={styles.notProvided}>Please check the data source and try again.</p>
        </div >
      </div>
    );
  }

  const {
    userid,
    name,
    email,
    phone,
    signupip,
    signupdeviceid,
    isresearchanalyst,
    ranumber,
    ravalidity,
    isblocked,
    blockingnote,
  } = user;

  const isRA = parseInt(isresearchanalyst) === 1;
  const isUserBlocked = parseInt(isblocked) === 1;

  // Format device ID for better display
  const formatDeviceId = (deviceId) => {
    if (!deviceId) return null;
    if (deviceId.length <= 20) return deviceId;
    return (
      <span title={deviceId}>
        {deviceId.substring(0, 17)}...
      </span>
    );
  };

  // Format RA validity date
  const formatRAValidity = (validity) => {
    if (!validity) return 'Not provided';
    try {
      const date = new Date(validity);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return validity;
    }
  };

  // Handle card interactions
  const handleCardClick = (e) => {
    // Don't expand if clicking on buttons or links
    if (e.target.closest('button') || e.target.closest('a')) {
      return;
    }
    if (onView) {
      onView(user);
    }
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit) onEdit(user);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) onDelete(user);
  };

  return (
    <div
      className={`${styles.userCard} ${isUserBlocked ? styles.blocked : ''}`}
      onClick={handleCardClick}
      role="article"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleCardClick(e);
        }
      }}
    >
      <div className={styles.cardHeader}>
        <div className={styles.userInfo}>
          <div className={styles.nameSection}>
            <h3 className={styles.userName}>
              {name || 'Unknown User'}
            </h3>
            <span className={styles.userId}>
              ID: {userid || 'N/A'}
            </span>
          </div>
          <div className={styles.userBadges}>
            {isRA && (
              <span className={styles.badge}>
                <span className={styles.badgeIcon}>👨‍💼</span>
                Research Analyst
              </span>
            )}
            <span className={`${styles.statusBadge} ${isUserBlocked ? styles.blocked : styles.active}`}>
              <span className={styles.statusDot}></span>
              {isUserBlocked ? 'Blocked' : 'Active'}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.cardContent}>
        {/* Contact Information */}
        <div className={styles.infoSection}>
          <h4 className={styles.sectionTitle}>
            📞 Contact Information
          </h4>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.label}>
                📧 Email:
              </span>
              <span className={styles.value}>
                {email ? (
                  <a
                    href={`mailto:${email}`}
                    className={styles.contactLink}
                    title={`Send email to ${email}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {email}
                  </a>
                ) : (
                  <span className={styles.notProvided}>Not provided</span>
                )}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.label}>
                📱 Phone:
              </span>
              <span className={styles.value}>
                {phone ? (
                  <a
                    href={`tel:${phone}`}
                    className={styles.contactLink}
                    title={`Call ${phone}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {phone}
                  </a>
                ) : (
                  <span className={styles.notProvided}>Not provided</span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Registration Details */}
        <div className={styles.infoSection}>
          <h4 className={styles.sectionTitle}>
            📋 Registration Details
          </h4>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.label}>
                🌐 Signup IP:
              </span>
              <span className={styles.value}>
                {signupip ? (
                  <code className={styles.codeValue}>
                    {signupip}
                  </code>
                ) : (
                  <span className={styles.notProvided}>Not provided</span>
                )}
              </span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.label}>
                📱 Device ID:
              </span>
              <span className={styles.value}>
                {signupdeviceid ? (
                  <code className={styles.codeValue}>
                    {formatDeviceId(signupdeviceid)}
                  </code>
                ) : (
                  <span className={styles.notProvided}>Not provided</span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Research Analyst Information */}
        {isRA && (
          <div className={styles.raInfo}>
            <div className={styles.infoSection}>
              <h4 className={styles.sectionTitle}>
                👨‍💼 Research Analyst Details
              </h4>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.label}>
                    🔢 RA Number:
                  </span>
                  <span className={styles.value}>
                    {ranumber ? (
                      <code className={styles.codeValue}>
                        {ranumber}
                      </code>
                    ) : (
                      <span className={styles.notProvided}>Not provided</span>
                    )}
                  </span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.label}>
                    📅 RA Validity:
                  </span>
                  <span className={styles.value}>
                    {ravalidity ? (
                      <span className={styles.validityDate}>
                        {formatRAValidity(ravalidity)}
                      </span>
                    ) : (
                      <span className={styles.notProvided}>Not provided</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Blocking Information */}
        {isUserBlocked && (
          <div className={styles.blockingInfo}>
            <div className={styles.infoSection}>
              <h4 className={styles.sectionTitle}>
                🚫 Blocking Information
              </h4>
              {blockingnote && (
                <div className={styles.blockingNote}>
                  <span className={styles.label}>
                    📝 Blocking Reason:
                  </span>
                  <p className={styles.blockingText}>
                    {blockingnote}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className={styles.cardFooter}>
        <div className={styles.cardActions}>
          <Button
            variant="primary"
            size="small"
            onClick={handleEdit}
            title={`Edit user ${name || userid}`}
            className={styles.editButton}
            aria-label={`Edit user ${name || userid}`}
          >
            ✏️ Edit
          </Button>
          <Button
            variant="danger"
            size="small"
            onClick={handleDelete}
            title={`Delete user ${name || userid}`}
            className={styles.deleteButton}
            aria-label={`Delete user ${name || userid}`}
          >
            🗑️ Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserCard;